import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, Save, Plus, Trash2, Image,
  Instagram, Linkedin, Twitter, Facebook, Youtube
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const platforms = [
  { value: 'instagram', label: 'Instagram', icon: Instagram },
  { value: 'linkedin', label: 'LinkedIn', icon: Linkedin },
  { value: 'twitter', label: 'Twitter/X', icon: Twitter },
  { value: 'facebook', label: 'Facebook', icon: Facebook },
  { value: 'youtube', label: 'YouTube', icon: Youtube },
];

const pageTypes = [
  { value: 'meme_page', label: 'Meme Page' },
  { value: 'influencer', label: 'Influencer' },
  { value: 'news_handle', label: 'News Handle' },
  { value: 'publication_social', label: 'Publication Social' },
];

const postTypes = [
  { value: 'feed_post', label: 'Feed Post' },
  { value: 'story', label: 'Story' },
  { value: 'reel', label: 'Reel' },
  { value: 'carousel', label: 'Carousel' },
  { value: 'short', label: 'Short (YouTube)' },
  { value: 'video', label: 'Video' },
];

const AdminSocialPageForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    handle: '',
    platform: 'instagram',
    page_type: 'meme_page',
    followers: 0,
    engagement_rate: '',
    categories: [],
    description: '',
    logo: '',
    post_types_available: ['feed_post'],
    is_active: true,
    is_featured: false,
    cost_price: '',
    vendor_id: '',
  });
  const [packages, setPackages] = useState([]);
  const [newPackage, setNewPackage] = useState({
    post_type: 'feed_post',
    name: '',
    price: '',
    cost_price: '',
    description: '',
    duration_hours: '',
    turnaround_days: 2,
  });
  const [categoryInput, setCategoryInput] = useState('');

  // Calculate margin preview
  const calculateMargin = (price, cost) => {
    const selling = parseFloat(price) || 0;
    const costPrice = parseFloat(cost) || 0;
    if (selling === 0) return { margin: 0, percent: 0, partnerMargin: 0, isAtRisk: false };
    
    const margin = selling - costPrice;
    const percent = (margin / selling) * 100;
    const partnerMin = selling * 0.82;
    const partnerMargin = partnerMin - costPrice;
    
    return { margin, percent: percent.toFixed(1), partnerMargin, isAtRisk: partnerMargin < 0 };
  };

  useEffect(() => {
    fetchVendors();
    if (isEditing) {
      fetchPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchVendors = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/vendors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVendors(response.data || []);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  const fetchPage = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/social-pages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const page = response.data.find(p => p.id === id);
      if (page) {
        setFormData({
          name: page.name || '',
          handle: page.handle || '',
          platform: page.platform || 'instagram',
          page_type: page.page_type || 'meme_page',
          followers: page.followers || 0,
          engagement_rate: page.engagement_rate || '',
          categories: page.categories || [],
          description: page.description || '',
          logo: page.logo || '',
          post_types_available: page.post_types_available || ['feed_post'],
          is_active: page.is_active !== false,
          is_featured: page.is_featured || false,
          cost_price: page.cost_price || '',
          vendor_id: page.vendor_id || '',
        });
        setPackages(page.packages || []);
      }
    } catch (error) {
      toast.error('Failed to load page');
      navigate('/admin/social-pages');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePostTypeToggle = (postType) => {
    setFormData(prev => ({
      ...prev,
      post_types_available: prev.post_types_available.includes(postType)
        ? prev.post_types_available.filter(t => t !== postType)
        : [...prev.post_types_available, postType]
    }));
  };

  const handleAddCategory = () => {
    if (categoryInput.trim() && !formData.categories.includes(categoryInput.trim())) {
      setFormData(prev => ({
        ...prev,
        categories: [...prev.categories, categoryInput.trim()]
      }));
      setCategoryInput('');
    }
  };

  const handleRemoveCategory = (cat) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.filter(c => c !== cat)
    }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const uploadData = new FormData();
    uploadData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/upload-image`, uploadData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setFormData(prev => ({ ...prev, logo: response.data.url }));
      toast.success('Logo uploaded');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload logo');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.handle) {
      toast.error('Name and handle are required');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      const payload = {
        ...formData,
        followers: parseInt(formData.followers) || 0,
        engagement_rate: formData.engagement_rate ? parseFloat(formData.engagement_rate) : null,
      };

      if (isEditing) {
        await axios.put(`${API}/admin/social-pages/${id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Social page updated');
      } else {
        await axios.post(`${API}/admin/social-pages`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Social page created');
      }
      
      navigate('/admin/social-pages');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleAddPackage = async () => {
    if (!newPackage.name || !newPackage.price) {
      toast.error('Package name and price are required');
      return;
    }

    if (!isEditing) {
      toast.error('Save the page first before adding packages');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/social-packages`, {
        social_page_id: id,
        ...newPackage,
        price: parseFloat(newPackage.price),
        cost_price: parseFloat(newPackage.cost_price) || 0,
        duration_hours: newPackage.duration_hours ? parseInt(newPackage.duration_hours) : null,
        turnaround_days: parseInt(newPackage.turnaround_days) || 2,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPackages([...packages, response.data]);
      setNewPackage({
        post_type: 'feed_post',
        name: '',
        price: '',
        cost_price: '',
        description: '',
        duration_hours: '',
        turnaround_days: 2,
      });
      toast.success('Package added');
    } catch (error) {
      toast.error('Failed to add package');
    }
  };

  const handleDeletePackage = async (packageId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/social-packages/${packageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPackages(packages.filter(p => p.id !== packageId));
      toast.success('Package deleted');
    } catch (error) {
      toast.error('Failed to delete package');
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl" data-testid="admin-social-page-form">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/admin/social-pages')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-[#1a1a1a]">
            {isEditing ? 'Edit Social Page' : 'Add Social Page'}
          </h1>
          <p className="text-sm text-gray-500">
            {isEditing ? 'Update social page details' : 'Add a new meme page, influencer, or social handle'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <div className="bg-white border border-gray-100 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-[#1a1a1a] mb-4">Basic Information</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Page Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., Startup Memes"
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37]"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Handle *</label>
              <input
                type="text"
                name="handle"
                value={formData.handle}
                onChange={handleChange}
                placeholder="e.g., startupmemes"
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37]"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Platform *</label>
              <select
                name="platform"
                value={formData.platform}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37] bg-white"
              >
                {platforms.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Page Type *</label>
              <select
                name="page_type"
                value={formData.page_type}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37] bg-white"
              >
                {pageTypes.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Followers</label>
              <input
                type="number"
                name="followers"
                value={formData.followers}
                onChange={handleChange}
                placeholder="e.g., 500000"
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Engagement Rate (%)</label>
              <input
                type="number"
                name="engagement_rate"
                value={formData.engagement_rate}
                onChange={handleChange}
                placeholder="e.g., 3.5"
                step="0.1"
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37]"
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              placeholder="Describe the page, audience, and content style..."
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37]"
            />
          </div>

          {/* Logo Upload */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Logo</label>
            <div className="flex items-center gap-4">
              {formData.logo ? (
                <img src={formData.logo} alt="Logo" className="w-16 h-16 rounded-full object-cover" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
                  <Image className="w-6 h-6 text-gray-400" />
                </div>
              )}
              <label className="px-4 py-2 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                <span className="text-sm">Upload Logo</span>
                <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
              </label>
            </div>
          </div>
        </div>

        {/* Categories */}
        <div className="bg-white border border-gray-100 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-[#1a1a1a] mb-4">Categories</h2>
          <div className="flex gap-2 mb-3">
            <input
              type="text"
              value={categoryInput}
              onChange={(e) => setCategoryInput(e.target.value)}
              placeholder="Add category..."
              className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37]"
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCategory())}
            />
            <button
              type="button"
              onClick={handleAddCategory}
              className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {formData.categories.map((cat, idx) => (
              <span key={idx} className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 rounded-full text-sm">
                {cat}
                <button type="button" onClick={() => handleRemoveCategory(cat)} className="text-gray-400 hover:text-red-500">
                  <Trash2 className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Post Types */}
        <div className="bg-white border border-gray-100 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-[#1a1a1a] mb-4">Available Post Types</h2>
          <div className="flex flex-wrap gap-2">
            {postTypes.map(type => (
              <button
                key={type.value}
                type="button"
                onClick={() => handlePostTypeToggle(type.value)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  formData.post_types_available.includes(type.value)
                    ? 'bg-[#D4AF37] text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {type.label}
              </button>
            ))}
          </div>
        </div>

        {/* Settings */}
        <div className="bg-white border border-gray-100 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-[#1a1a1a] mb-4">Settings</h2>
          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                className="w-4 h-4 text-[#D4AF37] rounded focus:ring-[#D4AF37]"
              />
              <span className="text-sm">Active</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                name="is_featured"
                checked={formData.is_featured}
                onChange={handleChange}
                className="w-4 h-4 text-[#D4AF37] rounded focus:ring-[#D4AF37]"
              />
              <span className="text-sm">Featured</span>
            </label>
          </div>
        </div>

        {/* Packages (only for editing) */}
        {isEditing && (
          <div className="bg-white border border-gray-100 rounded-xl p-6">
            <h2 className="text-lg font-semibold text-[#1a1a1a] mb-4">Packages</h2>
            
            {/* Existing Packages */}
            {packages.length > 0 && (
              <div className="space-y-3 mb-6">
                {packages.map(pkg => (
                  <div key={pkg.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-[#1a1a1a]">{pkg.name}</p>
                      <p className="text-sm text-gray-500">{pkg.post_type} • {pkg.turnaround_days} days</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-bold text-[#D4AF37]">₹{pkg.price?.toLocaleString()}</span>
                      <button
                        type="button"
                        onClick={() => handleDeletePackage(pkg.id)}
                        className="p-1 text-gray-400 hover:text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Add New Package */}
            <div className="border border-dashed border-gray-200 rounded-lg p-4">
              <p className="text-sm font-medium text-gray-700 mb-3">Add New Package</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <input
                  type="text"
                  value={newPackage.name}
                  onChange={(e) => setNewPackage({ ...newPackage, name: e.target.value })}
                  placeholder="Package name"
                  className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
                <select
                  value={newPackage.post_type}
                  onChange={(e) => setNewPackage({ ...newPackage, post_type: e.target.value })}
                  className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white"
                >
                  {postTypes.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
                <input
                  type="number"
                  value={newPackage.price}
                  onChange={(e) => setNewPackage({ ...newPackage, price: e.target.value })}
                  placeholder="Price (₹)"
                  className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-3">
                <input
                  type="number"
                  value={newPackage.cost_price}
                  onChange={(e) => setNewPackage({ ...newPackage, cost_price: e.target.value })}
                  placeholder="Cost Price (₹)"
                  className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
                <input
                  type="number"
                  value={newPackage.turnaround_days}
                  onChange={(e) => setNewPackage({ ...newPackage, turnaround_days: e.target.value })}
                  placeholder="TAT (days)"
                  className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
                <input
                  type="number"
                  value={newPackage.duration_hours}
                  onChange={(e) => setNewPackage({ ...newPackage, duration_hours: e.target.value })}
                  placeholder="Duration (hrs, for stories)"
                  className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
                <button
                  type="button"
                  onClick={handleAddPackage}
                  className="px-4 py-2 bg-[#D4AF37] text-white rounded-lg hover:bg-[#B8960F] text-sm"
                >
                  Add
                </button>
              </div>
              {/* Margin Preview */}
              {newPackage.price && newPackage.cost_price && (
                <div className={`mt-3 p-3 rounded-lg text-sm ${calculateMargin(newPackage.price, newPackage.cost_price).isAtRisk ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                  Margin: ₹{calculateMargin(newPackage.price, newPackage.cost_price).margin.toLocaleString()} ({calculateMargin(newPackage.price, newPackage.cost_price).percent}%)
                  {calculateMargin(newPackage.price, newPackage.cost_price).isAtRisk && ' ⚠️ Loss at partner pricing'}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Vendor Selection */}
        <div className="bg-white border border-gray-100 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-[#1a1a1a] mb-4">Cost & Vendor</h2>
          <p className="text-sm text-gray-500 mb-4">Track costs for margin analysis</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Default Cost Price (₹)</label>
              <input
                type="number"
                name="cost_price"
                value={formData.cost_price}
                onChange={handleChange}
                placeholder="What you pay to the page owner"
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vendor</label>
              <select
                name="vendor_id"
                value={formData.vendor_id}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37] bg-white"
              >
                <option value="">Select vendor</option>
                {vendors.map(v => (
                  <option key={v.id} value={v.id}>{v.name}</option>
                ))}
              </select>
              <a href="/admin/vendors" className="text-xs text-[#D4AF37] hover:underline mt-1 inline-block">Manage vendors →</a>
            </div>
          </div>
        </div>

        {/* Submit */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => navigate('/admin/social-pages')}
            className="px-6 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-[#D4AF37] text-white rounded-lg hover:bg-[#B8960F] disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {isEditing ? 'Save Changes' : 'Create Page'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminSocialPageForm;
